import { extract_error_message } from '@/utils/api_response_helpers';
import {
  create_main_section as create_main_section_request,
  create_sub_section as create_sub_section_request,
} from '@/services/main_section_service';

export function isValidOrderNumber(order_number) {
  const has_valid_order_number = Number.isInteger(order_number) && order_number >= 1;
  return has_valid_order_number;
}

export function resolveMainSectionCreateContext(ctx) {
  const create_context = {
    form_id: Number(ctx.topBar.formId),
    main_section_name: ctx.resolve_main_section_name(),
    order_number: ctx.new_main_section.order_number,
  };
  return create_context;
}

export function resolveSubSectionCreateContext(ctx) {
  const create_context = {
    form_id: Number(ctx.topBar.formId),
    form_main_section_id: Number(ctx.active_main_section_id) || null,
    order_number: ctx.new_sub_section.order_number,
    section_name: ctx.resolve_sub_section_name(),
    form_instructions: (Array.isArray(ctx.new_sub_section.form_instructions)
      ? ctx.new_sub_section.form_instructions
      : []).filter((instruction) => String(instruction?.content || '').trim()),
  };
  return create_context;
}

export function buildSubSectionPayload(create_context) {
  const payload = {
    form_id: create_context.form_id,
    section_name: create_context.section_name,
    order_number: create_context.order_number,
  };
  if (create_context.form_main_section_id) {
    payload.form_main_section_id = create_context.form_main_section_id;
  }
  payload.form_instructions = create_context.form_instructions;
  return payload;
}

export async function createMainSection(ctx) {
  const create_context = resolveMainSectionCreateContext(ctx);

  if (!create_context.form_id) {
    ctx.show_error('Please select a valid form first.');
    return;
  }

  ctx.creating_main_section = true;

  const loader = await ctx.show_loader('Creating main section...');

  try {
    await performCreateMainSection(ctx, create_context);
  } finally {
    await ctx.close_loader(loader);
    ctx.creating_main_section = false;
  }
}

async function performCreateMainSection(ctx, create_context) {
  try {
    const parsed_response = await create_main_section_request({
      main_section_name: create_context.main_section_name,
      order_number: create_context.order_number,
      form_id: create_context.form_id,
    });

    const success = parsed_response?.status === 'success';

    if (!success) {
      ctx.show_error(
        parsed_response?.message || 'Failed to create main section.'
      );
      return;
    }

    await ctx.load_reusable_section_catalogs();

    ctx.show_success(
      parsed_response.message || 'Main section created successfully.'
    );

    ctx.close_modal('create_main_section_modal');

    await ctx.load_main_sections();
  } catch (error) {
    ctx.show_error(
      extract_error_message(error, 'Failed to create main section.')
    );
  }
}

export async function createSubSection(ctx) {
  const create_context = resolveSubSectionCreateContext(ctx);

  if (!create_context.form_id) {
    ctx.show_error('Please select a valid form first.');
    return;
  }

  ctx.creating_main_section = true;

  const loader = await ctx.show_loader('Creating sub section...');

  try {
    await performCreateSubSection(ctx, create_context);
  } finally {
    await ctx.close_loader(loader);
    ctx.creating_main_section = false;
  }
}

async function performCreateSubSection(ctx, create_context) {
  try {
    const parsed_response = await create_sub_section_request(
      buildSubSectionPayload(create_context)
    );

    const success = parsed_response?.status === 'success';

    if (!success) {
      ctx.show_error(
        parsed_response?.message || 'Failed to create sub section.'
      );
      return;
    }

    await ctx.load_reusable_section_catalogs();
    await ctx.load_main_sections();

    ctx.show_success(
      parsed_response.message || 'Sub section created successfully.'
    );

    ctx.close_modal('create_sub_section_modal');
  } catch (error) {
    ctx.show_error(
      extract_error_message(error, 'Failed to create sub section.')
    );
  }
}
